import { useEffect, useRef, useState } from "react";
import type { PresenceLease } from "@dayvise/client-sdk";
import { useActiveWorkspaceId, useDayviseClient } from "./providers.tsx";

export type PresenceState = PresenceLease["state"];

export interface PresenceReporterOptions {

  readonly deviceId: string | null;
  readonly callCapable: boolean;
  readonly client: "web" | "desktop" | "android";
  readonly heartbeatMs?: number;

  readonly readVisibility?: () => { readonly visible: boolean; readonly focused: boolean };
  readonly now?: () => number;
}

const DEFAULT_HEARTBEAT_MS = 30_000;
const INTERACTION_THROTTLE_MS = 5_000;

export const PRESENCE_INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart"] as const;

export function visibilityFor(visible: boolean, focused: boolean): PresenceState {
  if (!visible) return "hidden";
  return focused ? "foreground" : "minimized";
}

export function usePresenceReporter(options: PresenceReporterOptions): { readonly state: PresenceState; readonly lastReportedAt: string | null } {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
  const now = options.now ?? (() => Date.now());

  const [state, setState] = useState<PresenceState>(visibilityFor(true, true));
  const [lastReportedAt, setLastReportedAt] = useState<string | null>(null);
  const sequence = useRef(0);
  // An open/restored tab is not evidence of user interaction.
  const interactedAt = useRef(new Date(0).toISOString());
  const lastSentInteraction = useRef(0);
  const readVisibilityRef = useRef(options.readVisibility);
  readVisibilityRef.current = options.readVisibility;
  const nowRef = useRef(now);
  nowRef.current = now;

  useEffect(() => {
    if (!options.deviceId || workspaceId === null) return;
    const readVisibility = (): { visible: boolean; focused: boolean } => readVisibilityRef.current?.() ?? {
      visible: typeof document === "undefined" || document.visibilityState === "visible",
      focused: typeof document === "undefined" || document.hasFocus(),
    };
    let stopped = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const send = async (next: PresenceState, interaction: string): Promise<void> => {
      if (stopped) return;
      // Restarting the app must not replay sequence 1 against a live lease.
      sequence.current = Math.max(sequence.current + 1, nowRef.current());
      try {
        await client.presence.update({
          deviceId: options.deviceId as string,
          state: next,
          callCapable: options.callCapable,
          sequence: sequence.current,
          interactedAt: interaction,
        });
        if (!stopped) setLastReportedAt(new Date(nowRef.current()).toISOString());
      } catch {

      }
    };

    const report = (): void => {
      const { visible, focused } = readVisibility();
      const next = visibilityFor(visible, focused);
      setState(next);
      void send(next, interactedAt.current);
    };

    const noteInteraction = (): void => {
      const current = nowRef.current();
      if (current - lastSentInteraction.current < INTERACTION_THROTTLE_MS) return;
      lastSentInteraction.current = current;
      interactedAt.current = new Date(current).toISOString();
      report();
    };

    const onVisibility = (): void => report();
    const onFocus = (): void => report();
    const onBlur = (): void => report();
    const onInteraction = (): void => noteInteraction();

    report();
    timer = setInterval(report, heartbeatMs);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
      document.addEventListener("focus", onFocus);
      document.addEventListener("blur", onBlur);
      for (const event of PRESENCE_INTERACTION_EVENTS) document.addEventListener(event, onInteraction, { passive: true });
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
      window.addEventListener("blur", onBlur);
    }

    return () => {
      stopped = true;
      if (timer !== undefined) clearInterval(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
        document.removeEventListener("focus", onFocus);
        document.removeEventListener("blur", onBlur);
        for (const event of PRESENCE_INTERACTION_EVENTS) document.removeEventListener(event, onInteraction);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("blur", onBlur);
      }
    };
  }, [client, heartbeatMs, options.callCapable, options.deviceId, workspaceId]);

  return { state, lastReportedAt };
}
